// Campaigns list — promotional giveaways. Search + status / credit-type
// filters + sort; rows link to the detail page. Inline create form (shared
// with the edit page) at the top. Credit type (Auto credit / Require visit)
// and Timing (Once / Daily / Weekly) are shown as two independent columns
// (SM-615); a legacy CLAIM row renders as Require visit.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  fetchCampaigns,
  fetchCampaignFundingOptions,
  createCampaign,
  keys,
} from "@/lib/queries";
import type { CampaignCreateInput } from "@/lib/queries";
import { FundingHealthBadge } from "@/components/campaigns/fundingHealth";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { campaignCurrency } from "@/lib/currency";
import { CREDIT_TYPE_LABEL, creditTypeOf, timingSummary } from "@/lib/campaigns";
import { CampaignForm } from "@/components/campaigns/CampaignForm";
import { useTableState } from "@/lib/useTableState";
import { fmtRelative } from "@/lib/format";

interface CampaignFilters {
  status: string;
  delivery: string;
}

export const Route = createFileRoute("/_app/campaigns/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: keys.campaignsList(undefined, undefined, undefined, 20, 0, {
        by: "created_at",
        dir: "desc",
      }),
      queryFn: () =>
        fetchCampaigns({ limit: 20, offset: 0, sort: { by: "created_at", dir: "desc" } }),
    }),
  component: CampaignsList,
});

function statusTone(s: string): "success" | "warning" | "muted" {
  return s === "ACTIVE" ? "success" : s === "DRAFT" ? "warning" : "muted";
}

function CampaignsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const table = useTableState<CampaignFilters>({
    defaultSort: { by: "created_at", dir: "desc" },
    defaultFilters: { status: "", delivery: "" },
  });
  useEffect(() => {
    table.setPage(0);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = q.trim();
  const campaigns = useQuery({
    queryKey: keys.campaignsList(
      trimmed || undefined,
      table.filters.status || undefined,
      table.filters.delivery || undefined,
      table.limit,
      table.offset,
      table.sort,
    ),
    queryFn: () =>
      fetchCampaigns({
        limit: table.limit,
        offset: table.offset,
        q: trimmed || undefined,
        status: table.filters.status || undefined,
        delivery: table.filters.delivery || undefined,
        sort: table.sort,
      }),
  });

  // Funding health per row (SM-194: an account that can't cover a reward
  // was invisible until users hit "Reward unavailable"). Null while
  // transfer funding is dark — the badge renders nothing.
  const funding = useQuery({
    queryKey: keys.campaignFundingOptions(),
    queryFn: fetchCampaignFundingOptions,
    staleTime: 60_000,
  });

  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (input: CampaignCreateInput) => createCampaign(input),
    onSuccess: async (c) => {
      setErr(null);
      setOpen(false);
      await qc.invalidateQueries({ queryKey: keys.campaigns });
      navigate({ to: "/campaigns/$id", params: { id: c.campaign_id } });
    },
    onError: (e) => setErr((e as Error).message),
    meta: { successMessage: "Campaign created." },
  });

  const items = campaigns.data?.items ?? [];
  const total = campaigns.data?.total ?? 0;
  const openDetail = (id: string) => navigate({ to: "/campaigns/$id", params: { id } });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">{total} campaigns</div>
        <Button onClick={() => setOpen((s) => !s)}>{open ? "Cancel" : "New campaign"}</Button>
      </div>

      {open && (
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>New campaign</CardTitle>
          </CardHeader>
          <CardBody className="pt-5">
            <CampaignForm
              mode="create"
              submitting={create.isPending}
              error={err}
              submitLabel="Create campaign"
              onSubmit={(input) => create.mutate(input)}
            />
          </CardBody>
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px] flex-1">
          <Input
            placeholder="Search campaigns by name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            placeholder="Status — any"
            value={table.filters.status}
            onChange={(e) => table.setFilter("status", e.target.value)}
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "DRAFT", label: "Draft" },
              { value: "DISABLED", label: "Disabled" },
            ]}
          />
        </div>
        <div className="w-44">
          <Select
            placeholder="Credit type — any"
            value={table.filters.delivery}
            onChange={(e) => table.setFilter("delivery", e.target.value)}
            options={[
              { value: "AUTO", label: CREDIT_TYPE_LABEL.AUTO },
              { value: "VISIT", label: CREDIT_TYPE_LABEL.VISIT },
            ]}
          />
        </div>
        {(table.hasActiveFilters || q) && (
          <Button
            variant="ghost"
            onClick={() => {
              setQ("");
              table.reset();
            }}
          >
            Reset
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Campaigns</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {campaigns.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {campaigns.data && items.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">
              {q || table.hasActiveFilters
                ? "No campaigns match the current filters."
                : "No campaigns yet."}
            </p>
          )}
          {campaigns.data && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <SortableHeader
                      label="Name"
                      field="name"
                      sort={table.sort}
                      onSortChange={table.setSort}
                      className="px-5"
                    />
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">
                      Credit type
                    </th>
                    <th className="px-3 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">
                      Reward
                    </th>
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">
                      Timing
                    </th>
                    <SortableHeader
                      label="Created"
                      field="created_at"
                      sort={table.sort}
                      onSortChange={table.setSort}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr
                      key={c.campaign_id}
                      onClick={() => openDetail(c.campaign_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openDetail(c.campaign_id);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open campaign ${c.name}`}
                      className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-neon/5 focus:bg-neon/5 focus:outline-none"
                    >
                      <td className="px-5 py-3 text-xs font-medium text-fg">{c.name}</td>
                      <td className="px-3 py-3">
                        <span className="flex items-center gap-1.5">
                          <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                          <FundingHealthBadge campaign={c} options={funding.data} />
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <Badge tone={creditTypeOf(c) === "VISIT" ? "accent" : "muted"}>
                          {CREDIT_TYPE_LABEL[creditTypeOf(c)]}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="inline-flex items-center gap-1.5">
                          <CurrencyAmount currency={c.currency} value={c.reward_amount_minor} />
                          {campaignCurrency(c.currency) !== "GOLD" && (
                            <CurrencyBadge currency={c.currency} />
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-2xs text-muted">{timingSummary(c)}</td>
                      <td className="px-3 py-3 text-right text-2xs text-muted">
                        {fmtRelative(c.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {campaigns.data && total > 0 && (
            <div className="flex items-center justify-between border-t border-border px-5 py-3 text-2xs text-muted">
              <span>
                Showing {table.offset + 1}–{table.offset + items.length} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={table.page === 0}
                  onClick={() => table.setPage(table.page - 1)}
                >
                  Prev
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={table.offset + items.length >= total}
                  onClick={() => table.setPage(table.page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
